
import type { ReferentFormData } from '@/types/referentForm';
import { logger } from '@/utils/productionLogger';

/**
 * Soumission atomique de formulaire référent via fonction RPC PostgreSQL
 * Garantit la cohérence entre insertion de la réponse et mise à jour du statut
 */
export const submitReferentResponse = async (
  referent: { id: string },
  campaign: { id: string },
  formData: ReferentFormData,
  fingerprintData?: { fingerprintConsent: boolean; deviceFingerprint: string | null }
): Promise<void> => {
  // 1. Validation des paramètres d'entrée
  if (!referent || !campaign) {
    throw new Error('Données manquantes pour la soumission');
  }

  logger.info('🚀 Début soumission référent (RPC atomique)', {
    referentId: referent.id,
    campaignId: campaign.id,
    hasAudio: !!formData.audioFileUrl,
    hasSignature: !!formData.signedDocumentUrl,
    criteriaCount: Object.keys(formData.ratings || {}).length
  });

  // 2. Préparer les données pour la fonction RPC
  const evaluationData = {
    relationTypeConfirmed: formData.relationTypeConfirmed,
    collaborationStartDate: formData.collaborationStartDate,
    collaborationEndDate: formData.collaborationEndDate,
    stillWorksAtCompany: formData.stillWorksAtCompany,
    stillWorkingTogether: formData.stillWorkingTogether, // ✅ AJOUT : Champ manquant pour la logique des dates
    ratings: formData.ratings,
    criticalQuestions: formData.criticalQuestions,
    audioFileUrl: formData.audioFileUrl,
    audioTranscription: formData.audioTranscription,
    // FIX: Extraire les données enrichies depuis enrichedData (retourné par transcribe-audio)
    cleanedTranscription: formData.enrichedData?.cleaned_transcription || null,
    transcriptionSentiment: formData.enrichedData?.sentiment || null,
    transcriptionSentimentReason: formData.enrichedData?.sentiment_reason || null,
    transcriptionKeyPoints: formData.enrichedData?.points_cles || null,
    signedDocumentUrl: formData.signedDocumentUrl,
  };

  // 3. Appel via Edge Function pour capturer l'IP côté serveur
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-referent-evaluation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          referentId: referent.id,
          campaignId: campaign.id,
          evaluationData: evaluationData,
          deviceFingerprint: fingerprintData?.deviceFingerprint,
          fingerprintConsent: fingerprintData?.fingerprintConsent
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      logger.error('❌ Erreur Edge Function submit-referent-evaluation', errorData, {
        status: response.status,
        referent_id: referent.id,
        campaign_id: campaign.id
      });
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Erreur inconnue');
    }

    const { data, error } = result;

    if (error) {
      logger.error('❌ Erreur fonction RPC submit_referent_evaluation', error, {
        referent_id: referent.id,
        campaign_id: campaign.id
      });
      throw new Error(error.message);
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Erreur lors de la soumission');
    }

    logger.info('✅ Soumission référent réussie', {
      referentId: referent.id,
      campaignId: campaign.id,
      responseId: data?.response_id,
      executionTime: data?.execution_time_ms + 'ms',
      message: data?.message,
      hasFingerprint: !!fingerprintData?.deviceFingerprint,
      fingerprintConsent: fingerprintData?.fingerprintConsent
    });

  } catch (error: any) {
    logger.error('💥 Erreur lors de la soumission référent', error, {
      referent_id: referent.id,
      campaign_id: campaign.id
    });
    
    // Re-throw avec message plus explicite
    throw new Error(error?.message || 'Erreur lors de la soumission de l\'évaluation');
  }
};
